/**
 * Lazy namespace loading for i18n
 * 
 * This module provides functions to dynamically import translation namespaces
 * to reduce the initial bundle size. Only 'common', 'auth', and 'legal' are
 * loaded initially; other namespaces are loaded on-demand when routes are visited.
 */

type Namespace = 'common' | 'auth' | 'legal' | 'navigation' | 'listings' | 'offers' | 'profile' | 'admin' | 'platform' | 'allocations';
type Language = 'en' | 'sv';

// Track loading state per (language, namespace) to prevent duplicate imports
const loadingState: Map<string, Promise<Record<string, unknown>>> = new Map();
const loadedState: Set<string> = new Set();

function getKey(lang: Language, ns: Namespace): string {
  return `${lang}:${ns}`;
}

/**
 * Check if a namespace is already loaded for a given language
 */
export function isNamespaceLoaded(lang: Language, ns: Namespace): boolean {
  return loadedState.has(getKey(lang, ns));
}

/**
 * Mark a namespace as loaded (used for initial namespaces bundled synchronously)
 */
export function markAsLoaded(lang: Language, ns: Namespace): void {
  loadedState.add(getKey(lang, ns));
}

/**
 * Dynamically import a namespace for a given language.
 * Returns the translation data or null if the import fails.
 * 
 * Features:
 * - Deduplication: Multiple calls for the same (lang, ns) return the same promise
 * - Fail-safe: Errors are caught and logged, returns null instead of crashing
 */
export async function loadNamespace(
  lang: Language,
  ns: Namespace
): Promise<Record<string, unknown> | null> {
  const key = getKey(lang, ns);
  
  // Already loaded
  if (loadedState.has(key)) {
    return null;
  }
  
  // Already loading - return existing promise
  const existingPromise = loadingState.get(key);
  if (existingPromise) {
    return existingPromise;
  }
  
  // Start loading
  const promise = importNamespace(lang, ns);
  loadingState.set(key, promise);
  
  try {
    const result = await promise;
    loadedState.add(key);
    loadingState.delete(key);
    return result;
  } catch (error) {
    console.warn(`[i18n] Failed to load namespace "${ns}" for language "${lang}":`, error);
    loadingState.delete(key);
    return null;
  }
}

/**
 * Dynamic import functions for each namespace.
 * Vite will create separate chunks for each.
 */
async function importNamespace(
  lang: Language,
  ns: Namespace
): Promise<Record<string, unknown>> {
  // Use explicit paths for Vite to analyze and create chunks
  if (lang === 'en') {
    switch (ns) {
      case 'navigation':
        return (await import('./locales/en/navigation.json')).default;
      case 'listings':
        return (await import('./locales/en/listings.json')).default;
      case 'offers':
        return (await import('./locales/en/offers.json')).default;
      case 'profile':
        return (await import('./locales/en/profile.json')).default;
      case 'admin':
        return (await import('./locales/en/admin.json')).default;
      case 'platform':
        return (await import('./locales/en/platform.json')).default;
      case 'allocations':
        return (await import('./locales/en/allocations.json')).default;
      // Initial namespaces - shouldn't be called but handle gracefully
      case 'common':
        return (await import('./locales/en/common.json')).default;
      case 'auth':
        return (await import('./locales/en/auth.json')).default;
      case 'legal':
        return (await import('./locales/en/legal.json')).default;
    }
  } else {
    switch (ns) {
      case 'navigation':
        return (await import('./locales/sv/navigation.json')).default;
      case 'listings':
        return (await import('./locales/sv/listings.json')).default;
      case 'offers':
        return (await import('./locales/sv/offers.json')).default;
      case 'profile':
        return (await import('./locales/sv/profile.json')).default;
      case 'admin':
        return (await import('./locales/sv/admin.json')).default;
      case 'platform':
        return (await import('./locales/sv/platform.json')).default;
      case 'allocations':
        return (await import('./locales/sv/allocations.json')).default;
      // Initial namespaces - shouldn't be called but handle gracefully
      case 'common':
        return (await import('./locales/sv/common.json')).default;
      case 'auth':
        return (await import('./locales/sv/auth.json')).default;
      case 'legal':
        return (await import('./locales/sv/legal.json')).default;
    }
  }
}

/**
 * Get the list of lazy namespaces (not loaded initially)
 */
export const LAZY_NAMESPACES: Namespace[] = ['navigation', 'listings', 'offers', 'profile', 'admin', 'platform', 'allocations'];

/**
 * Get the list of initial namespaces (loaded synchronously)
 */
export const INITIAL_NAMESPACES: Namespace[] = ['common', 'auth', 'legal'];
